import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderTree,
  Newspaper,
  Plus,
  Trash,
  Edit3,
  X,
  AlignLeft,
  CheckCircle2,
} from 'lucide-react';
import React, { useState } from 'react';
import ReactQuill from 'react-quill-new';
import { Link } from 'react-router-dom';

import 'react-quill-new/dist/quill.snow.css';

import { Button, EmptyState, Spinner, Badge, Input } from '@terra/ui';

import { api } from '../lib/api';

// ══════════════════════════════════════════════════════════════════
// BLOG SYSTEM
// ══════════════════════════════════════════════════════════════════

export function BlogCategoriesPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });

  const categories = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.blogs.categories.list(),
  });

  const createCat = useMutation({
    mutationFn: () => api.blogs.categories.create({ ...formData, active: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
      setShowAdd(false);
      setFormData({ name: '', description: '' });
    },
  });

  const deleteCat = useMutation({
    mutationFn: (id: string) => api.blogs.categories.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
    },
  });

  const rows = categories.data ?? [];

  return (
    <div className="space-y-6 pb-8">
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-ink-muted flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
                <FolderTree className="h-4 w-4" /> Create Category
              </h3>
            </div>

            <form
              className="grid items-start gap-4 sm:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (formData.name) createCat.mutate();
              }}
            >
              <div>
                <Input
                  label="Category Name"
                  placeholder="e.g. Tech News"
                  value={formData.name}
                  onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
                  required
                />
              </div>
              <div className="sm:row-span-2">
                <label className="text-ink mb-1 block text-[13px] font-bold">
                  Description (Optional)
                </label>
                <textarea
                  className="border-border/60 bg-surface text-ink placeholder:text-ink-subtle focus:border-ink focus:ring-ink/20 w-full rounded-xl border px-3 py-2.5 text-[13px] font-medium outline-none transition-colors focus:ring-2"
                  rows={4}
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="mt-auto flex justify-end sm:col-start-1">
                <Button
                  type="submit"
                  loading={createCat.isPending}
                  disabled={!formData.name}
                  className="h-10 w-full"
                >
                  Create Category
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Toolbar */}
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <FolderTree className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">All Categories</span>
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ New Category'}
          </button>
        </div>

        <div className="p-0">
          {categories.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-6 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<FolderTree className="text-ink-muted h-10 w-10" />}
                title="No categories found"
                description="Create a category to start organizing your blog."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Name</th>
                    <th className="py-3 pr-4">Slug</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((r: any, idx: number) => (
                    <tr key={r.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">{r.name}</td>
                      <td className="py-3 pr-4">
                        <span className="bg-surface-subtle text-ink-muted border-border/40 inline-flex items-center justify-center rounded-md border px-2 py-0.5 font-mono text-[11px] font-bold">
                          {r.slug}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-center">
                        <Badge tone={r.active ? 'success' : 'neutral'}>
                          {r.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 pr-5">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Delete category ${r.name}?`))
                                deleteCat.mutate(r.id);
                            }}
                            className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash className="h-[18px] w-[18px]" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function BlogPostsPage() {
  const queryClient = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    categoryId: '',
    excerpt: '',
    content: '',
    published: false,
  });

  const posts = useQuery({
    queryKey: ['blog-posts'],
    queryFn: () => api.blogs.posts.list(),
  });

  const categories = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.blogs.categories.list(),
  });

  const createPost = useMutation({
    mutationFn: () => api.blogs.posts.create(formData),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      setShowAdd(false);
      setFormData({ title: '', categoryId: '', excerpt: '', content: '', published: false });
    },
  });

  const deletePost = useMutation({
    mutationFn: (id: string) => api.blogs.posts.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
    },
  });

  const rows = posts.data ?? [];
  const catRows = categories.data ?? [];

  return (
    <div className="space-y-6 pb-8">
      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="border-border/60 bg-surface overflow-hidden rounded-2xl border p-6 shadow-sm"
          >
            <div className="border-border/40 mb-6 flex items-center justify-between border-b pb-4">
              <h3 className="text-ink flex items-center gap-2 text-[15px] font-extrabold">
                <Edit3 className="text-ink h-4 w-4" /> Write New Article
              </h3>
            </div>

            <form
              className="grid items-start gap-6 sm:grid-cols-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (formData.title && formData.categoryId && formData.content) createPost.mutate();
              }}
            >
              <div>
                <Input
                  label="Title"
                  placeholder="Post title"
                  value={formData.title}
                  onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>
              <div>
                <label className="text-ink mb-1 block text-[13px] font-bold">Category</label>
                <select
                  className="border-border/60 bg-surface text-ink focus:border-ink focus:ring-ink/20 h-10 w-full rounded-xl border px-3 text-[13px] font-medium outline-none transition-colors focus:ring-2"
                  value={formData.categoryId}
                  onChange={(e) => setFormData((f) => ({ ...f, categoryId: e.target.value }))}
                  required
                >
                  <option value="">-- Select a Category --</option>
                  {catRows.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="text-ink mb-1 block text-[13px] font-bold">
                  Excerpt (Optional)
                </label>
                <textarea
                  className="border-border/60 bg-surface text-ink placeholder:text-ink-subtle focus:border-ink focus:ring-ink/20 w-full rounded-xl border px-3 py-2.5 text-[13px] font-medium outline-none transition-colors focus:ring-2"
                  rows={2}
                  placeholder="A short summary of the post..."
                  value={formData.excerpt}
                  onChange={(e) => setFormData((f) => ({ ...f, excerpt: e.target.value }))}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="text-ink mb-1 block text-[13px] font-bold">Content</label>
                <div className="border-border/60 bg-surface focus-within:border-ink focus-within:ring-ink/20 [&_.ql-toolbar]:border-border/60 [&_.ql-toolbar]:bg-surface-subtle/30 [&_.ql-editor]:text-ink overflow-hidden rounded-xl border transition-all focus-within:ring-2 [&_.ql-container]:border-0 [&_.ql-editor]:min-h-[250px] [&_.ql-editor]:text-[13px] [&_.ql-editor]:font-medium [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b">
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(v: string) => setFormData({ ...formData, content: v })}
                  />
                </div>
              </div>

              <div className="border-border/40 mt-2 flex items-center justify-between border-t pt-4 sm:col-span-2">
                <label className="group flex cursor-pointer items-center gap-2">
                  <div
                    className={`grid h-5 w-5 place-items-center rounded border transition-colors ${formData.published ? 'border-ink bg-ink text-white' : 'border-border/60 bg-surface group-hover:border-ink text-transparent'}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </div>
                  <input
                    type="checkbox"
                    checked={formData.published}
                    onChange={(e) => setFormData((f) => ({ ...f, published: e.target.checked }))}
                    className="hidden"
                  />
                  <span className="text-ink text-[13px] font-bold">Publish immediately</span>
                </label>

                <Button
                  type="submit"
                  loading={createPost.isPending}
                  disabled={!formData.title || !formData.categoryId || !formData.content}
                  className="h-10 px-8"
                >
                  Create Post
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
        {/* Toolbar */}
        <div className="border-border/60 bg-surface-subtle/30 flex items-center justify-between border-b px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-surface-subtle text-ink-muted ring-border/60 grid h-8 w-8 place-items-center rounded-lg ring-1 ring-inset">
              <Newspaper className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="text-ink text-[14px] font-bold">All Published & Draft Posts</span>
          </div>
          <button
            onClick={() => setShowAdd((v) => !v)}
            className="bg-ink hover:bg-ink/90 flex h-10 items-center justify-center rounded-xl px-4 text-[13px] font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
          >
            {showAdd ? 'Close Form' : '+ New Blog Post'}
          </button>
        </div>

        <div className="p-0">
          {posts.isLoading ? (
            <div className="grid min-h-[300px] place-items-center">
              <Spinner size="lg" />
            </div>
          ) : rows.length === 0 ? (
            <div className="border-border/60 bg-surface-subtle/30 m-6 rounded-2xl border border-dashed py-12">
              <EmptyState
                icon={<Newspaper className="text-ink-muted h-10 w-10" />}
                title="No posts found"
                description="Write an article to get your blog started."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-border/60 text-ink-muted bg-surface/50 border-b text-[10px] font-bold uppercase tracking-wider">
                    <th className="w-16 py-3 pl-5 pr-4">#</th>
                    <th className="py-3 pr-4">Title</th>
                    <th className="py-3 pr-4">Category</th>
                    <th className="py-3 pr-4 text-center">Status</th>
                    <th className="py-3 pr-4 text-right">Published On</th>
                    <th className="py-3 pr-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-border/40 divide-y text-[13px]">
                  {rows.map((r: any, idx: number) => (
                    <tr key={r.id} className="hover:bg-surface-subtle/30 transition-colors">
                      <td className="text-ink-muted py-3 pl-5 pr-4 font-mono text-[11px] font-bold">
                        {(idx + 1).toString().padStart(2, '0')}
                      </td>
                      <td className="text-ink py-3 pr-4 font-semibold">{r.title}</td>
                      <td className="text-ink-muted py-3 pr-4 font-bold">{r.category?.name}</td>
                      <td className="py-3 pr-4 text-center">
                        <Badge tone={r.published ? 'success' : 'neutral'}>
                          {r.published ? 'Published' : 'Draft'}
                        </Badge>
                      </td>
                      <td className="text-ink-muted py-3 pr-4 text-right text-[12px] font-medium">
                        {new Date(r.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-3 pr-5">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm(`Delete post "${r.title}"?`))
                                deletePost.mutate(r.id);
                            }}
                            className="text-ink-muted flex h-8 w-8 items-center justify-center rounded-lg transition-colors hover:bg-red-50 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash className="h-[18px] w-[18px]" strokeWidth={2} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
